/**
 * sentryScrub — nothing secret leaves the device through Sentry.
 *
 * WHY (TASK-FE-AUTH-CALLBACK-NO-TOKEN-LOGS-SENTRY-REDACTION-2026-10-09, operator security audit):
 * the implicit OAuth flow returns access_token / refresh_token / provider_token in the URL FRAGMENT
 * of /auth/callback. Sentry records that URL on its own — as a navigation breadcrumb, as the
 * event's request url, as span data — and copies every console line into a breadcrumb. Sentry.init
 * had no redaction at all, so one error during a login shipped a rep's session to a third party.
 * Removing the logs that printed the URL is the cure at the source; this is the net under it.
 *
 * ONE SCRUBBER (coordinator ruling): the strings go through the SAME redactor the bug-report bundle
 * uses (components/common/debugReportRedact) — tokens, JWTs, Bearer values, and also e-mail
 * addresses, phone numbers and IBANs. On top of it every URL is cut at its '#': a fragment is
 * never needed to debug, and it is where the tokens travel.
 *
 * Wired in main.tsx as beforeBreadcrumb / beforeSend / beforeSendTransaction. Each hook is total:
 * a value it cannot scrub is DROPPED (breadcrumb / field), never passed through unscrubbed, and
 * a scrubber fault never loses the error event itself — it is sent with the unscrubbable parts
 * removed.
 */
import type { Breadcrumb, BrowserOptions, ErrorEvent } from '@sentry/react'
import { redactDeep, redactString, SENSITIVE_QUERY } from '../components/common/debugReportRedact'

/** The performance event Sentry hands to beforeSendTransaction (@sentry/react does not export the name). */
export type TransactionEvent = Parameters<NonNullable<BrowserOptions['beforeSendTransaction']>>[0]

/** An absolute or root-relative URL followed by a fragment, inside any string. */
const URL_WITH_FRAGMENT_RE = /((?:https?:\/\/|\/)[^\s"'<>#]*)#[^\s"'<>]*/g

/**
 * One `key=value` of a URL query, wherever it stands in a string: after `?` or `&`. Codex P1 #2216:
 * the fragment cut and the shared string redactor leave `?code=<authorization code>` untouched —
 * `code` is a secret only by its POSITION in a query (the bug-report redactor knows that in
 * redactUrl, which takes a whole URL; here the URL sits inside a message, a breadcrumb, a span).
 */
const QUERY_PARAM_RE = /([?&])([A-Za-z0-9_.~-]+)=([^&\s"'<>#]*)/g

/**
 * Query keys whose value is a PERSON, not a credential (Codex P1 #2216). The app puts the rep's
 * e-mail in request URLs through encodeURIComponent (`?email=scout%40…`, useMorningSync), so the
 * value has no literal `@` for a pattern to find: the KEY is what marks it. Same names as the
 * shared redactor's object-key list.
 */
const PII_QUERY = new Set<string>(['email', 'mail', 'e_mail', 'phone', 'tel', 'telefon', 'mobile', 'handy'])

/**
 * Keys whose VALUE is a credential whatever it looks like — matched as a SUBSTRING, so the shapes a
 * transport gives them are all covered (csrf_token, provider_refresh_token, Set-Cookie, cookies,
 * X-Api-Key …). Wider than the bug-report redactor's exact-name list on purpose: here the keys are
 * chosen by HTTP and by the SDK, not by us. Used for OBJECT keys and for QUERY keys alike.
 */
const CREDENTIAL_KEY_RE = /token|secret|passw|authorization|cookie|api[_-]?key|session/i

/**
 * `state` is the OAuth anti-forgery value that travels with `code`; not in the shared list, redacted
 * here. A query key is also sensitive when its NAME is a credential name (Codex P1 #2216): the agent
 * sign-in lands on `/signin?session=<token>&email=…` (agentSession.ts), and `session` is on no exact
 * list. The substring match is deliberately the wide one: `session_id`, `sessionType`, `tokenized`
 * lose their value too. Less context in an error-tracker URL is the accepted price (coordinator ruling).
 */
const isSensitiveQueryKey = (key: string): boolean => {
  const k = key.toLowerCase()
  return k === 'state' || SENSITIVE_QUERY.has(k) || PII_QUERY.has(k) || CREDENTIAL_KEY_RE.test(k)
}

/** Blank the value of every sensitive query parameter (shared list: code, token, key, sig, auth … + the PII keys). */
function redactQueryParams(input: string): string {
  return input.replace(QUERY_PARAM_RE, (match, lead: string, key: string) => (isSensitiveQueryKey(key) ?`${lead}${key}=[REDACTED]` : match))
}

/**
 * A bare query string, as Sentry carries it in `request.query_string` (`code=…&state=…`, no leading
 * `?`): the first pair has no delimiter in front of it, so it is scrubbed as the query it is.
 */
export function scrubQueryString(query: string): string {
  return scrubString(`?${query}`).slice(1)
}

/** `request.query_string` in each shape Sentry allows: a string, a key→value object, or [key, value] pairs. */
function scrubQuery(query: unknown): unknown {
  if (typeof query === 'string') return scrubQueryString(query)
  if (Array.isArray(query)) {
    return query.map((pair) => (
      Array.isArray(pair) && typeof pair[0] === 'string' && isSensitiveQueryKey(pair[0]) ? [pair[0], '[REDACTED]'] : scrubValue(pair)
    ))
  }
  if (query !== null && typeof query === 'object') {
    return Object.fromEntries(
      Object.entries(query as Record<string, unknown>).map(([k, v]) => [k, isSensitiveQueryKey(k) ? '[REDACTED]' : scrubValue(v)]),
    )
  }
  return query
}

/** Cut URL fragments, blank sensitive query parameters, then redact secrets + PII. The fragment is removed, not masked. */
export function scrubString(input: string): string {
  return redactString(redactQueryParams(input.replace(URL_WITH_FRAGMENT_RE, '$1')))
}

/**
 * Deep scrub of an arbitrary JSON-like value: a credential key's value is blanked; everything else
 * goes through the shared redactor's object walk (its sensitive-key list + every string), and
 * every string additionally loses its URL fragments.
 */
export function scrubValue(value: unknown): unknown {
  if (typeof value === 'string') return scrubString(value)
  if (Array.isArray(value)) return value.map(scrubValue)
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (CREDENTIAL_KEY_RE.test(k)) out[k] = '[REDACTED]'
      else if (k === 'query_string') out[k] = scrubQuery(v)
      else if (v !== null && typeof v === 'object') out[k] = scrubValue(v)
      // A scalar under a plain key: the shared walk decides by key name (email, phone, iban …).
      else out[k] = scrubValue((redactDeep({ [k]: v }) as Record<string, unknown>)[k])
    }
    return out
  }
  return value
}

/**
 * beforeBreadcrumb. Returns the scrubbed breadcrumb, or null (dropped) when it cannot be scrubbed —
 * a breadcrumb is context, and context that may carry a token is worth less than nothing.
 */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  try {
    const out: Breadcrumb = { ...breadcrumb }
    if (typeof out.message === 'string') out.message = scrubString(out.message)
    if (out.data !== undefined) out.data = scrubValue(out.data) as Breadcrumb['data']
    return out
  } catch {
    return null
  }
}

/** The React-strict-mode / cancelled-request noise the app never reported (unchanged rule from main.tsx). */
function isAbortNoise(event: ErrorEvent): boolean {
  const msgValue = event.exception?.values?.[0]?.value
  const msg = msgValue !== null && msgValue !== undefined ? msgValue : ''
  return msg.includes('AbortError') || msg.includes('signal is aborted')
}

/** The free-form parts of an event, scrubbed in place on a shallow copy. Shared by both event kinds. */
function scrubEventFields<E extends ErrorEvent | TransactionEvent>(event: E): E {
  const out: E = { ...event }
  if (typeof out.message === 'string') out.message = scrubString(out.message)
  if (typeof out.transaction === 'string') out.transaction = scrubString(out.transaction)
  if (out.request !== undefined) out.request = scrubValue(out.request) as E['request']
  if (out.extra !== undefined) out.extra = scrubValue(out.extra) as E['extra']
  if (out.contexts !== undefined) out.contexts = scrubValue(out.contexts) as E['contexts']
  if (out.tags !== undefined) out.tags = scrubValue(out.tags) as E['tags']
  if (out.user !== undefined) out.user = scrubValue(out.user) as E['user']
  if (out.breadcrumbs !== undefined) {
    out.breadcrumbs = out.breadcrumbs.map(scrubBreadcrumb).filter((b): b is Breadcrumb => b !== null)
  }
  // The WHOLE exception, not only its message (Codex P1 #2216): a stack frame of inline or eval'd
  // code names the page it ran on — filename / abs_path are the callback URL, fragment included —
  // and mechanism.data and frame vars are free-form. The same holds for thread stacktraces and for
  // the script URLs listed in debug_meta.
  if (out.exception?.values !== undefined) {
    out.exception = { ...out.exception, values: out.exception.values.map((v) => scrubValue(v) as typeof v) }
  }
  if (out.threads !== undefined) out.threads = scrubValue(out.threads) as E['threads']
  if (out.debug_meta !== undefined) out.debug_meta = scrubValue(out.debug_meta) as E['debug_meta']
  if (out.spans !== undefined) {
    out.spans = out.spans.map((span) => ({
      ...span,
      ...(typeof span.description === 'string' ? { description: scrubString(span.description) } : {}),
      ...(span.data !== undefined ? { data: scrubValue(span.data) as typeof span.data } : {}),
    })) as E['spans']
  }
  return out
}

/** What is left of an event whose free-form parts could not be scrubbed: its identity, nothing else. */
function bareEvent<E extends ErrorEvent | TransactionEvent>(event: E): E {
  const bare = {
    type: event.type,
    event_id: event.event_id,
    timestamp: event.timestamp,
    level: event.level,
    platform: event.platform,
    release: event.release,
    environment: event.environment,
    message: 'event content removed: it could not be scrubbed (sentryScrub)',
  }
  return bare as unknown as E
}

/** beforeSend. Drops the abort noise (as before); otherwise sends the scrubbed event. */
export function scrubEvent(event: ErrorEvent): ErrorEvent | null {
  if (isAbortNoise(event)) return null
  try {
    return scrubEventFields(event)
  } catch {
    return bareEvent(event)
  }
}

/** beforeSendTransaction. Performance events carry the page URL and every request URL as span data. */
export function scrubTransaction(event: TransactionEvent): TransactionEvent | null {
  try {
    return scrubEventFields(event)
  } catch {
    return null
  }
}
